import { useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import { api, API, fmtDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Plus, Pencil, Trash2, Camera, Loader2, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { ROLE_LABELS, SPECIALTIES, SPECIALTY_LABELS } from "@/lib/auth";

const emptyPay = { banco: "", agencia: "", conta: "", tipo_conta: "Corrente", titular: "", cpf_titular: "", pix_chave: "" };
const emptyUser = {
  name: "", username: "", email: "", password: "",
  role: "administrativo", seller_id: "", client_id: "",
  active: true, photo_url: "",
  chat_allowed: [], payment_info: { ...emptyPay },
  specialties: [],
};

export default function Users() {
  const [items, setItems] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyUser);
  const [tab, setTab] = useState("dados");
  const [photoUploading, setPhotoUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const [u, s] = await Promise.all([api.get("/users"), api.get("/sellers")]);
    setItems(u.data); setSellers(s.data); setAllUsers(u.data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyUser, payment_info: { ...emptyPay }, chat_allowed: [] });
    setTab("dados");
    setOpen(true);
  };

  const openEdit = async (u) => {
    setEditing(u);
    setTab("dados");
    setOpen(true);
    // Load full data (chat_allowed + payment_info)
    try {
      const r = await api.get(`/users/${u.id}/full`);
      const full = r.data || {};
      setForm({
        ...emptyUser,
        ...full.user,
        password: "",
        chat_allowed: full.chat_allowed || [],
        payment_info: { ...emptyPay, ...(full.payment_info || {}) },
        specialties: full.user?.specialties || [],
      });
    } catch {
      setForm({ ...emptyUser, ...u, password: "", chat_allowed: [], payment_info: { ...emptyPay } });
    }
  };

  const save = async () => {
    try {
      const payload = {
        name: form.name,
        role: form.role,
        active: form.active,
        chat_allowed: form.chat_allowed || [],
        payment_info: form.payment_info,
        photo_url: form.photo_url || "",
        specialties: form.specialties || [],
      };
      if (form.role === "vendedor") payload.seller_id = form.seller_id || "";
      else payload.seller_id = "";
      if (form.role === "autor") payload.client_id = form.client_id || "";
      else payload.client_id = "";

      if (editing) {
        if (form.username && form.username !== editing.username) payload.username = form.username;
        if (form.email !== editing.email) payload.email = form.email || "";
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
      } else {
        if (!form.username) return toast.error("Informe o usuário (login)");
        if (!form.password) return toast.error("Informe a senha");
        if (form.role === "vendedor" && !form.seller_id) return toast.error("Selecione o vendedor vinculado");
        payload.username = form.username;
        payload.password = form.password;
        payload.email = form.email || "";
        await api.post("/users", payload);
      }
      toast.success("Salvo");
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Excluir usuário ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Excluído"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const sName = (id) => sellers.find(s => s.id === id)?.nome || "";

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Selecione uma imagem"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Imagem > 5MB"); return; }
    setPhotoUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await fetch(`${API}/files/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        credentials: "include",
        body: fd,
      });
      if (!r.ok) throw new Error("upload");
      const data = await r.json();
      const url = `${API}/files/${data.id}/download`;
      setForm(f => ({ ...f, photo_url: url }));
      toast.success("Foto enviada — não esqueça de salvar o usuário");
    } catch { toast.error("Falha ao enviar foto"); }
    finally {
      setPhotoUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggleChat = (uid) => {
    setForm(f => {
      const s = new Set(f.chat_allowed || []);
      if (s.has(uid)) s.delete(uid); else s.add(uid);
      return { ...f, chat_allowed: Array.from(s) };
    });
  };

  const initials = (form.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <Layout title="Usuários" subtitle="Gestão de usuários, perfis, foto, pagamento e permissões de chat">
      <div className="flex justify-end mb-4">
        <Button data-testid="user-new" onClick={openCreate}><Plus className="w-4 h-4 mr-1"/>Novo Usuário</Button>
      </div>
      <Card className="rounded-sm overflow-hidden">
        <Table>
          <TableHeader className="bg-secondary/60"><TableRow>
            <TableHead></TableHead>
            <TableHead>Usuário</TableHead><TableHead>Nome</TableHead><TableHead>E-mail</TableHead>
            <TableHead>Perfil</TableHead><TableHead>Status</TableHead><TableHead>Criado</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow></TableHeader>
          <TableBody>{items.map(u => (
            <TableRow key={u.id}>
              <TableCell>
                <Avatar className="h-8 w-8">
                  {u.photo_url ? <AvatarImage src={u.photo_url} /> : null}
                  <AvatarFallback className="text-[10px]">
                    {(u.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </TableCell>
              <TableCell className="font-mono text-sm font-semibold">{u.username || "—"}</TableCell>
              <TableCell>{u.name}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{u.email || "—"}</TableCell>
              <TableCell><Badge variant="secondary">{ROLE_LABELS[u.role]}{u.role === "vendedor" && u.seller_id && ` (${sName(u.seller_id)})`}</Badge></TableCell>
              <TableCell><Badge variant={u.active ? "default" : "secondary"}>{u.active ? "Ativo" : "Inativo"}</Badge></TableCell>
              <TableCell className="text-xs text-muted-foreground">{fmtDate(u.created_at)}</TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`}><Pencil className="w-4 h-4"/></Button>
                <Button variant="ghost" size="sm" onClick={() => remove(u)} data-testid={`user-delete-${u.id}`}><Trash2 className="w-4 h-4"/></Button>
              </TableCell>
            </TableRow>
          ))}</TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar" : "Novo"} Usuário</DialogTitle>
            <DialogDescription>
              Dados de acesso, foto, pagamento e permissões individuais de chat.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid w-full grid-cols-4" data-testid="user-tabs">
              <TabsTrigger value="dados" data-testid="tab-dados">Dados</TabsTrigger>
              <TabsTrigger value="foto" data-testid="tab-foto">Foto</TabsTrigger>
              <TabsTrigger value="pagamento" data-testid="tab-pagamento">Pagamento</TabsTrigger>
              <TabsTrigger value="chat" data-testid="tab-chat">Chat</TabsTrigger>
            </TabsList>

            {/* DADOS */}
            <TabsContent value="dados" className="space-y-3 mt-3">
              <div><Label className="text-xs font-semibold">Nome completo</Label>
                <Input data-testid="user-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1"/>
              </div>
              <div>
                <Label className="text-xs font-semibold">Usuário (login)</Label>
                <Input data-testid="user-username" value={form.username || ""} onChange={(e) => setForm({...form, username: e.target.value.toLowerCase().replace(/\s+/g, "")})} placeholder="ex.: joao.silva" className="mt-1"/>
                <p className="text-[10px] text-muted-foreground mt-1">Sem espaços. Será convertido para minúsculo.</p>
              </div>
              <div><Label className="text-xs font-semibold">E-mail (opcional)</Label>
                <Input data-testid="user-email" type="email" value={form.email || ""} onChange={(e) => setForm({...form, email: e.target.value})} className="mt-1"/>
              </div>
              <div><Label className="text-xs font-semibold">Senha {editing && "(deixar vazio p/ manter)"}</Label>
                <Input data-testid="user-password" type="password" value={form.password || ""} onChange={(e) => setForm({...form, password: e.target.value})} className="mt-1"/>
              </div>
              <div><Label className="text-xs font-semibold">Perfil</Label>
                <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                  <SelectTrigger className="mt-1" data-testid="user-role"><SelectValue/></SelectTrigger>
                  <SelectContent>{Object.entries(ROLE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.role === "vendedor" && (
                <div>
                  <Label className="text-xs font-semibold">Vendedor vinculado</Label>
                  <Select value={form.seller_id || ""} onValueChange={(v) => setForm({...form, seller_id: v})}>
                    <SelectTrigger className="mt-1" data-testid="user-seller"><SelectValue placeholder="Selecione o vendedor"/></SelectTrigger>
                    <SelectContent>{sellers.map(s => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground mt-1">Este login só verá os dados desse vendedor (somente leitura).</p>
                </div>
              )}
              {form.role === "prestador" && (
                <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
                  Perfil &quot;Prestador de Serviço&quot; é exclusivo para chat — ele só verá Projetos Editoriais e Mensagens.
                </div>
              )}
              <div className="flex items-center gap-2">
                <input type="checkbox" id="active" checked={form.active} onChange={(e) => setForm({...form, active: e.target.checked})} />
                <Label htmlFor="active" className="text-xs font-semibold">Ativo</Label>
              </div>
            </TabsContent>

            {/* FOTO */}
            <TabsContent value="foto" className="space-y-4 mt-3">
              <div className="flex items-center gap-4 py-2">
                <Avatar className="h-24 w-24 ring-2 ring-[hsl(var(--primary)/0.3)]">
                  {form.photo_url ? <AvatarImage src={form.photo_url} /> : null}
                  <AvatarFallback className="text-3xl bg-primary text-primary-foreground">{initials}</AvatarFallback>
                </Avatar>
                <div className="flex flex-col gap-2">
                  <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickFile} data-testid="user-photo-input" />
                  <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={photoUploading} data-testid="user-photo-upload">
                    {photoUploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Camera className="w-3.5 h-3.5 mr-1" />}
                    {form.photo_url ? "Trocar foto" : "Enviar foto"}
                  </Button>
                  {form.photo_url && (
                    <Button size="sm" variant="ghost" onClick={() => setForm(f => ({...f, photo_url: ""}))} className="text-red-600" data-testid="user-photo-remove">
                      <Trash2 className="w-3.5 h-3.5 mr-1" />Remover
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">JPG, PNG ou WebP até 5MB. A foto só fica permanente após salvar o usuário.</p>
            </TabsContent>

            {/* PAGAMENTO */}
            <TabsContent value="pagamento" className="space-y-3 mt-3">
              <p className="text-[11px] text-muted-foreground">Dados usados em comissões, royalties e pagamentos.</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-semibold">Titular</Label>
                  <Input data-testid="user-pay-titular" value={form.payment_info?.titular || ""} onChange={(e) => setForm({...form, payment_info: {...form.payment_info, titular: e.target.value}})} className="mt-1" />
                </div>
                <div><Label className="text-xs font-semibold">CPF do titular</Label>
                  <Input data-testid="user-pay-cpf" value={form.payment_info?.cpf_titular || ""} onChange={(e) => setForm({...form, payment_info: {...form.payment_info, cpf_titular: e.target.value}})} className="mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-semibold">Banco</Label>
                  <Input data-testid="user-pay-banco" value={form.payment_info?.banco || ""} onChange={(e) => setForm({...form, payment_info: {...form.payment_info, banco: e.target.value}})} className="mt-1" />
                </div>
                <div><Label className="text-xs font-semibold">Tipo</Label>
                  <Select value={form.payment_info?.tipo_conta || "Corrente"} onValueChange={(v) => setForm({...form, payment_info: {...form.payment_info, tipo_conta: v}})}>
                    <SelectTrigger className="mt-1" data-testid="user-pay-tipo"><SelectValue/></SelectTrigger>
                    <SelectContent><SelectItem value="Corrente">Corrente</SelectItem><SelectItem value="Poupança">Poupança</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-semibold">Agência</Label>
                  <Input data-testid="user-pay-agencia" value={form.payment_info?.agencia || ""} onChange={(e) => setForm({...form, payment_info: {...form.payment_info, agencia: e.target.value}})} className="mt-1" />
                </div>
                <div><Label className="text-xs font-semibold">Conta</Label>
                  <Input data-testid="user-pay-conta" value={form.payment_info?.conta || ""} onChange={(e) => setForm({...form, payment_info: {...form.payment_info, conta: e.target.value}})} className="mt-1" />
                </div>
              </div>
              <div><Label className="text-xs font-semibold">Chave PIX</Label>
                <Input data-testid="user-pay-pix" value={form.payment_info?.pix_chave || ""} onChange={(e) => setForm({...form, payment_info: {...form.payment_info, pix_chave: e.target.value}})} className="mt-1" placeholder="CPF, e-mail, telefone ou aleatória" />
              </div>
            </TabsContent>

            {/* CHAT */}
            <TabsContent value="chat" className="space-y-3 mt-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] text-muted-foreground">
                  <MessageSquare className="inline w-3 h-3 mr-1" />
                  Marque com quem este usuário pode iniciar conversa. Permissões são unilaterais.
                </p>
                <div className="flex gap-1">
                  <Button data-testid="user-chat-select-all" size="sm" variant="outline" onClick={() => {
                    setForm(f => ({ ...f, chat_allowed: allUsers.filter(u => u.id !== editing?.id).map(u => u.id) }));
                  }}>Marcar todos</Button>
                  <Button data-testid="user-chat-clear" size="sm" variant="ghost" onClick={() => setForm(f => ({ ...f, chat_allowed: [] }))}>Limpar</Button>
                </div>
              </div>
              <Separator />
              <div className="max-h-[300px] overflow-y-auto border border-border rounded-sm divide-y divide-border">
                {allUsers.filter(u => u.id !== editing?.id).length === 0 && (
                  <div className="p-4 text-xs text-muted-foreground text-center">
                    {editing ? "Nenhum outro usuário disponível" : "Salve o usuário primeiro para liberar as permissões individuais"}
                  </div>
                )}
                {allUsers.filter(u => u.id !== editing?.id).map(u => {
                  const checked = (form.chat_allowed || []).includes(u.id);
                  return (
                    <label key={u.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-secondary/30">
                      <Checkbox
                        data-testid={`user-chat-${u.id}`}
                        checked={checked}
                        onCheckedChange={() => toggleChat(u.id)}
                      />
                      <Avatar className="h-7 w-7">
                        {u.photo_url ? <AvatarImage src={u.photo_url} /> : null}
                        <AvatarFallback className="text-[10px]">{(u.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{u.name}</div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{ROLE_LABELS[u.role] || u.role}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
              <p className="text-[10px] text-muted-foreground">
                Total selecionado: <b>{(form.chat_allowed || []).length}</b> pessoa(s).
              </p>
            </TabsContent>
          </Tabs>

          <DialogFooter><Button onClick={save} data-testid="user-save">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
